import type { Question, QuestionCategory } from '@/types';

// Re-export date utilities for backwards compatibility
export { formatDate, formatDateTime, formatRelativeTime } from './dateUtils';

// =============================================================================
// QUESTION NUMBERING UTILITIES
// =============================================================================

/**
 * Computes section-based numbering for questions with hierarchical sub-numbering for conditional questions.
 *
 * Numbering scheme:
 * - General (uncategorized) questions: simple sequential numbers (1, 2, 3)
 * - Categorized questions: section.index format (1.1, 1.2, 2.1, 2.2)
 * - Conditional questions: parent.subindex format (1.1.1, 1.1.2)
 *
 * @param questions - All questions sorted by displayOrder
 * @param categories - All categories for the survey
 * @returns Record mapping question ID to its display number string
 *
 * @example
 * const numbers = computeQuestionNumbers(questions, categories);
 * // { 1: "1.1", 2: "1.2", 3: "2.1", 4: "1.1.1" }
 */
export function computeQuestionNumbers(
  questions: Question[],
  categories: QuestionCategory[]
): Record<number, string> {
  const numbers: Record<number, string> = {};

  // Sort questions by displayOrder
  const sortedQuestions = [...questions].sort((a, b) => a.displayOrder - b.displayOrder);

  // Sort categories by displayOrder
  const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  // Separate parent questions (no displayConditions) from conditional questions
  const isConditionalQuestion = (q: Question) =>
    q.displayConditions && q.displayConditions.length > 0;

  const parentQuestions = sortedQuestions.filter(q => !isConditionalQuestion(q));
  const conditionalQuestions = sortedQuestions.filter(q => isConditionalQuestion(q));

  // Group parent questions by category
  const questionsByCategory: Record<number | 'general', Question[]> = { general: [] };
  sortedCategories.forEach((cat) => {
    questionsByCategory[cat.id] = [];
  });

  parentQuestions.forEach((q) => {
    if (q.categoryId && questionsByCategory[q.categoryId]) {
      questionsByCategory[q.categoryId].push(q);
    } else {
      questionsByCategory.general.push(q);
    }
  });

  // First pass: Assign numbers to parent questions
  // General questions get simple numbers (no section prefix)
  // Categorized questions use section.questionIndex format

  // Handle questions without a category (General section)
  if (questionsByCategory.general.length > 0) {
    questionsByCategory.general.forEach((q, idx) => {
      numbers[q.id] = `${idx + 1}`;
    });
  }

  // Handle each category in order - section numbers start at 1
  let sectionIndex = 0;
  sortedCategories.forEach((cat) => {
    const catQuestions = questionsByCategory[cat.id];
    if (catQuestions.length > 0) {
      sectionIndex++;
      catQuestions.forEach((q, idx) => {
        numbers[q.id] = `${sectionIndex}.${idx + 1}`;
      });
    }
  });

  // Second pass: Assign hierarchical numbers to conditional questions
  // Group conditional questions by their parent question
  const childrenByParent: Record<number, Question[]> = {};
  conditionalQuestions.forEach((q) => {
    const parentId = q.displayConditions![0].dependsOnQuestionId;
    if (!childrenByParent[parentId]) {
      childrenByParent[parentId] = [];
    }
    childrenByParent[parentId].push(q);
  });

  // Assign sub-numbers to each conditional question
  Object.entries(childrenByParent).forEach(([parentIdStr, children]) => {
    const parentId = Number(parentIdStr);
    const parentNumber = numbers[parentId];

    if (parentNumber) {
      // Sort children by displayOrder and assign sub-numbers
      children.sort((a, b) => a.displayOrder - b.displayOrder);
      children.forEach((child, idx) => {
        numbers[child.id] = `${parentNumber}.${idx + 1}`;
      });
    } else {
      // Parent doesn't have a number (might also be conditional)
      // For now, use a fallback numbering
      children.forEach((child, idx) => {
        numbers[child.id] = `?.${idx + 1}`;
      });
    }
  });

  return numbers;
}

// =============================================================================
// TYPES
// =============================================================================

/**
 * Answer data structure for progress calculation
 * Matches the AnswerData interface in PublicSurvey.tsx
 */
export interface AnswerData {
  questionId: number;
  freeText?: string;
  selectedOptionIds?: number[];
  rankedSelections?: { optionId: number; rank: number }[];
  isOtherSelected?: boolean;
  otherText?: string;
}

/**
 * Progress calculation result
 */
export interface SurveyProgress {
  /** Number of questions with valid answers */
  answered: number;
  /** Total number of visible questions */
  total: number;
  /** Completion percentage (0-100) */
  percentage: number;
  /** Estimated minutes remaining */
  estimatedMinutesRemaining: number;
}

/**
 * Section progress calculation result
 */
export interface SectionProgress {
  /** Number of answered questions in section */
  answered: number;
  /** Total questions in section */
  total: number;
  /** Whether all required questions are answered */
  isComplete: boolean;
}

/**
 * Formatted answer for review display
 */
export interface FormattedAnswer {
  /** Display text for the answer */
  displayText: string;
  /** Whether question has a valid answer */
  hasAnswer: boolean;
  /** Answer type for styling */
  type: 'text' | 'selection' | 'ranked' | 'empty';
}

/**
 * Determines if a question should be displayed based on conditional logic
 * @param question The question to check
 * @param _allQuestions All questions in the survey (reserved for future complex conditions)
 * @param answers Current answers for all questions
 * @returns true if the question should be shown
 */
export function shouldShowQuestion(
  question: Question,
  _allQuestions: Question[],
  answers: Record<number, { selectedOptionIds?: number[]; freeText?: string }>
): boolean {
  const conditions = question.displayConditions || [];

  // If no conditions, always show
  if (conditions.length === 0) {
    return true;
  }

  // All conditions must be met (AND logic)
  return conditions.every((condition) => {
    const dependsOnAnswer = answers[condition.dependsOnQuestionId];
    if (!dependsOnAnswer) return false;

    const selectedOptions = dependsOnAnswer.selectedOptionIds || [];
    return selectedOptions.includes(condition.requiredOptionId);
  });
}

/**
 * Validation error with translation key support
 */
export interface ValidationError {
  questionId: number;
  /** Translation key for the error message */
  messageKey: string;
  /** Parameters for interpolation (e.g., { min: 3, max: 5 }) */
  params?: Record<string, number | string>;
  /** Fallback message if translation is not available */
  fallbackMessage: string;
}

/**
 * Validates if all required questions in a phase are answered
 * @param questions Questions in the current phase
 * @param answers Current answers
 * @param allQuestions All questions (for conditional display)
 * @returns Object with isValid and errors array with translation keys
 */
export function validatePhase(
  questions: Question[],
  answers: Record<number, { selectedOptionIds?: number[]; freeText?: string; rankedSelections?: { optionId: number; rank: number }[]; isOtherSelected?: boolean; otherText?: string }>,
  allQuestions: Question[]
): { isValid: boolean; errors: ValidationError[] } {
  const errors: ValidationError[] = [];

  for (const question of questions) {
    // Skip validation if question is not shown due to conditions
    if (!shouldShowQuestion(question, allQuestions, answers)) {
      continue;
    }

    const answer = answers[question.id];
    // Support both nested questionType.code and direct questionTypeCode
    const typeCode = question.questionTypeCode || question.questionType?.code;

    // Validate "Other" text for ALL questions (required or not) if user selected "Other"
    // If a user actively selects "Other", they must provide text regardless of isRequired
    if (answer && !question.isRequired) {
      const hasOtherSelected = (() => {
        if (typeCode === 'RANKED_CHOICE') {
          const otherOpt = question.options?.find((o) => o.isOtherOption);
          return otherOpt
            ? answer.rankedSelections?.some((rs) => rs.optionId === otherOpt.id)
            : answer.isOtherSelected;
        }
        return answer.isOtherSelected;
      })();

      if (hasOtherSelected && (!answer.otherText || answer.otherText.trim().length === 0)) {
        errors.push({
          questionId: question.id,
          messageKey: 'public.validation.otherTextRequired',
          fallbackMessage: 'Please provide text for your "Other" selection',
        });
      }
      continue;
    }

    if (!answer) {
      if (question.isRequired) {
        errors.push({
          questionId: question.id,
          messageKey: 'public.validation.required',
          fallbackMessage: 'This question requires an answer',
        });
      }
      continue;
    }

    switch (typeCode) {
      case 'OPEN_TEXT':
        if (!answer.freeText || answer.freeText.trim().length === 0) {
          errors.push({
            questionId: question.id,
            messageKey: 'public.validation.provideAnswer',
            fallbackMessage: 'Please provide an answer',
          });
        }
        break;

      case 'SINGLE_CHOICE':
        // For single choice: valid if regular option selected OR "Other" with text
        {
          const otherOpt = question.options?.find((o) => o.isOtherOption);
          // Count only regular options (exclude Other option ID)
          const regularSelectedIds = otherOpt
            ? (answer.selectedOptionIds || []).filter(id => id !== otherOpt.id)
            : (answer.selectedOptionIds || []);
          const hasSelectedRegularOption = regularSelectedIds.length > 0;

          if (!hasSelectedRegularOption && !answer.isOtherSelected) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.selectOption',
              fallbackMessage: 'Please select an option',
            });
          }
          // If "Other" is selected, require the text to be filled
          if (answer.isOtherSelected && (!answer.otherText || answer.otherText.trim().length === 0)) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.otherTextRequired',
              fallbackMessage: 'Please provide text for your "Other" selection',
            });
          }
        }
        break;

      case 'MULTIPLE_CHOICE':
        // For multiple choice: valid if any options selected OR "Other" with text
        {
          const otherOpt = question.options?.find((o) => o.isOtherOption);
          // Count only regular options (exclude Other option ID)
          const regularCount = otherOpt
            ? (answer.selectedOptionIds || []).filter(id => id !== otherOpt.id).length
            : (answer.selectedOptionIds?.length || 0);
          const hasOtherAnswer = answer.isOtherSelected && answer.otherText && answer.otherText.trim().length > 0;
          const totalSelections = regularCount + (hasOtherAnswer ? 1 : 0);

          if (totalSelections === 0 && !answer.isOtherSelected) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.selectOption',
              fallbackMessage: 'Please select an option',
            });
          }
          // If "Other" is selected, require the text to be filled
          if (answer.isOtherSelected && (!answer.otherText || answer.otherText.trim().length === 0)) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.otherTextRequired',
              fallbackMessage: 'Please provide text for your "Other" selection',
            });
          }
          // Check min/max selections (count "Other" with text as one selection)
          if (question.minSelections && totalSelections < question.minSelections) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.minSelections',
              params: { min: question.minSelections },
              fallbackMessage: `Please select at least ${question.minSelections} options`,
            });
          }
          if (question.maxSelections && totalSelections > question.maxSelections) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.maxSelections',
              params: { max: question.maxSelections },
              fallbackMessage: `Please select no more than ${question.maxSelections} options`,
            });
          }
        }
        break;

      case 'TRUE_FALSE':
      case 'SCALE5':
        if (!answer.selectedOptionIds || answer.selectedOptionIds.length === 0) {
          errors.push({
            questionId: question.id,
            messageKey: 'public.validation.selectOption',
            fallbackMessage: 'Please select an option',
          });
        }
        break;

      case 'RANKED_CHOICE':
        {
          const rankedCount = answer.rankedSelections?.length || 0;
          const minRequired = question.minSelections || 1;
          // Find the real "Other" option ID from question options
          const otherOption = question.options?.find((o) => o.isOtherOption);
          const hasOtherInRanking = otherOption
            ? answer.rankedSelections?.some((rs) => rs.optionId === otherOption.id)
            : answer.isOtherSelected;
          const hasValidOtherText = answer.otherText && answer.otherText.trim().length > 0;

          if (rankedCount < minRequired) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.minRanked',
              params: { min: minRequired },
              fallbackMessage: `Please rank at least ${minRequired} options`,
            });
          }
          // If "Other" is ranked, require the text to be filled
          if (hasOtherInRanking && !hasValidOtherText) {
            errors.push({
              questionId: question.id,
              messageKey: 'public.validation.otherTextRequired',
              fallbackMessage: 'Please provide text for your "Other" selection',
            });
          }
        }
        break;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// =============================================================================
// PROGRESS CALCULATION UTILITIES
// =============================================================================

/**
 * Calculates survey completion progress based on visible questions and answers.
 *
 * A question is considered "answered" if:
 * - OPEN_TEXT: has non-empty trimmed text
 * - SINGLE_CHOICE: has selected option OR "Other" with text
 * - MULTIPLE_CHOICE: has selected options OR "Other" with text
 * - TRUE_FALSE, SCALE5: has at least one selected option
 * - RANKED_CHOICE: has at least minSelections ranked items
 *
 * @param answers - Current answer data for all questions
 * @param visibleQuestions - Questions currently visible (after conditional filtering)
 * @returns Progress object with counts, percentage, and time estimate
 *
 * @example
 * const progress = calculateSurveyProgress(answers, visibleQuestions);
 * // { answered: 5, total: 10, percentage: 50, estimatedMinutesRemaining: 2 }
 */
export function calculateSurveyProgress(
  answers: Record<number, AnswerData>,
  visibleQuestions: Question[]
): SurveyProgress {
  // Filter out SECTION type questions - they're structural elements, not answerable questions
  const answerableQuestions = visibleQuestions.filter(q => {
    const typeCode = q.questionTypeCode || q.questionType?.code;
    return typeCode !== 'SECTION';
  });

  let answered = 0;
  const total = answerableQuestions.length;

  for (const question of answerableQuestions) {
    const answer = answers[question.id];
    if (!answer) continue;

    const typeCode = question.questionTypeCode || question.questionType?.code;
    let isAnswered = false;

    switch (typeCode) {
      case 'OPEN_TEXT':
        isAnswered = !!(answer.freeText && answer.freeText.trim().length > 0);
        break;

      case 'SINGLE_CHOICE':
        // Valid if option selected OR "Other" with text
        {
          const hasSelectedOption = answer.selectedOptionIds && answer.selectedOptionIds.length > 0;
          const hasOtherAnswer = answer.isOtherSelected && answer.otherText && answer.otherText.trim().length > 0;
          isAnswered = !!(hasSelectedOption || hasOtherAnswer);
        }
        break;

      case 'MULTIPLE_CHOICE':
        // Valid if any options selected OR "Other" with text
        {
          const hasSelectedOptions = answer.selectedOptionIds && answer.selectedOptionIds.length > 0;
          const hasOtherAnswer = answer.isOtherSelected && answer.otherText && answer.otherText.trim().length > 0;
          isAnswered = !!(hasSelectedOptions || hasOtherAnswer);
        }
        break;

      case 'TRUE_FALSE':
      case 'SCALE5':
        isAnswered = !!(answer.selectedOptionIds && answer.selectedOptionIds.length > 0);
        break;

      case 'RANKED_CHOICE': {
        const minRequired = question.minSelections || 1;
        isAnswered = !!(answer.rankedSelections && answer.rankedSelections.length >= minRequired);
        break;
      }
    }

    if (isAnswered) answered++;
  }

  const percentage = total > 0 ? Math.round((answered / total) * 100) : 0;
  const remainingQuestions = total - answered;
  // Estimate ~20 seconds per question
  const estimatedMinutesRemaining = Math.max(0, Math.ceil((remainingQuestions * 20) / 60));

  return {
    answered,
    total,
    percentage,
    estimatedMinutesRemaining,
  };
}

/**
 * Filters questions to only those that should be visible based on display conditions.
 *
 * Uses the existing shouldShowQuestion function internally but provides
 * a convenient wrapper for filtering entire question lists.
 *
 * @param questions - All questions (sorted by displayOrder)
 * @param answers - Current answer data for condition evaluation
 * @returns Filtered array of visible questions
 *
 * @example
 * const visible = getVisibleQuestions(allQuestions, answers);
 */
export function getVisibleQuestions(
  questions: Question[],
  answers: Record<number, { selectedOptionIds?: number[]; freeText?: string }>
): Question[] {
  return questions.filter((q) => shouldShowQuestion(q, questions, answers));
}

/**
 * Formats estimated time remaining for display.
 *
 * @param minutesRemaining - Estimated minutes remaining
 * @returns Formatted time string (e.g., "~3 min", "Less than 1 min")
 *
 * @example
 * formatTimeRemaining(5)  // "~5 min"
 * formatTimeRemaining(0)  // "Less than 1 min"
 * formatTimeRemaining(1)  // "~1 min"
 */
export function formatTimeRemaining(minutesRemaining: number): string {
  if (minutesRemaining <= 0) {
    return 'Less than 1 min';
  }
  if (minutesRemaining === 1) {
    return '~1 min';
  }
  return `~${minutesRemaining} min`;
}

/**
 * Calculates completion status for a specific section/category.
 *
 * @param questions - Questions in the section
 * @param answers - Current answer data
 * @returns Object with answered count and whether section is complete
 *
 * @example
 * const status = calculateSectionProgress(sectionQuestions, answers);
 * // { answered: 3, total: 5, isComplete: false }
 */
export function calculateSectionProgress(
  questions: Question[],
  answers: Record<number, AnswerData>
): SectionProgress {
  const progress = calculateSurveyProgress(answers, questions);

  // Check if all required questions are answered
  const requiredQuestions = questions.filter((q) => q.isRequired);
  const requiredProgress = calculateSurveyProgress(answers, requiredQuestions);

  return {
    answered: progress.answered,
    total: progress.total,
    isComplete: requiredProgress.answered === requiredProgress.total,
  };
}

/**
 * Formats an answer for display in the review page.
 *
 * @param question - The question being answered
 * @param answer - The answer data
 * @returns Formatted answer with display text and metadata
 *
 * @example
 * const formatted = formatAnswerForReview(question, answer);
 * // { displayText: "Option A, Option B", hasAnswer: true, type: "selection" }
 */
export function formatAnswerForReview(
  question: Question,
  answer: AnswerData | undefined
): FormattedAnswer {
  if (!answer) {
    return { displayText: 'Not answered', hasAnswer: false, type: 'empty' };
  }

  const typeCode = question.questionTypeCode || question.questionType?.code;
  const options = question.options || [];

  switch (typeCode) {
    case 'OPEN_TEXT':
      if (answer.freeText && answer.freeText.trim()) {
        return { displayText: answer.freeText.trim(), hasAnswer: true, type: 'text' };
      }
      break;

    case 'SINGLE_CHOICE':
      // Check for regular option selection
      if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
        const selectedOption = options.find((o) => o.id === answer.selectedOptionIds![0]);
        const label = selectedOption?.label || 'Unknown option';
        return { displayText: label, hasAnswer: true, type: 'selection' };
      }
      // Check for "Other" answer
      if (answer.isOtherSelected && answer.otherText && answer.otherText.trim()) {
        return { displayText: `Other: ${answer.otherText.trim()}`, hasAnswer: true, type: 'text' };
      }
      break;

    case 'TRUE_FALSE':
    case 'SCALE5':
      if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
        const selectedOption = options.find((o) => o.id === answer.selectedOptionIds![0]);
        const label = selectedOption?.label || 'Unknown option';
        return { displayText: label, hasAnswer: true, type: 'selection' };
      }
      break;

    case 'MULTIPLE_CHOICE':
      {
        const selectedLabels: string[] = [];
        // Add regular selected options
        if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
          answer.selectedOptionIds.forEach((id) => {
            const option = options.find((o) => o.id === id);
            selectedLabels.push(option?.label || 'Unknown');
          });
        }
        // Add "Other" if selected
        if (answer.isOtherSelected && answer.otherText && answer.otherText.trim()) {
          selectedLabels.push(`Other: ${answer.otherText.trim()}`);
        }
        if (selectedLabels.length > 0) {
          return { displayText: selectedLabels.join(', '), hasAnswer: true, type: 'selection' };
        }
      }
      break;

    case 'RANKED_CHOICE':
      if (answer.rankedSelections && answer.rankedSelections.length > 0) {
        const rankedLabels = answer.rankedSelections
          .sort((a, b) => a.rank - b.rank)
          .map((rs) => {
            // Handle "Other" option (ID -999)
            if (rs.optionId === -999) {
              const otherText = answer.otherText?.trim() || 'Other';
              return `${rs.rank}. Other: ${otherText}`;
            }
            const option = options.find((o) => o.id === rs.optionId);
            return `${rs.rank}. ${option?.label || 'Unknown'}`;
          })
          .join('\n');
        return { displayText: rankedLabels, hasAnswer: true, type: 'ranked' };
      }
      break;
  }

  return { displayText: 'Not answered', hasAnswer: false, type: 'empty' };
}

